import type { Maybe } from '@adornis/base/utilTypes.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { hoverTooltip } from '@adornis/popover/hover-tooltip.js';
import { formatCalendarDay, toCalendarDay, todayAsCalendarDay } from '@adornis/datetime/calendarDay.js';
import { Collapse } from '@adornis/components/client/Collapse.js';
import { addMonths, differenceInCalendarMonths, startOfMonth } from 'date-fns';
import { html, nothing } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import {
  resolveAxisEnd,
  resolveAxisStart,
  type SectionInterval,
  sectionIntervals,
} from '../../api/contractTimelineAxis.js';
import { getSupplementStatusForDate } from '../../api/getSupplementStatus.js';
import type { TimelineContract, TimelineSupplement } from './ContractTimeline.js';
import { SUPPLEMENT_SECTION_LABEL, SUPPLEMENT_STATUS_PRESENTATION } from './status/supplementStatusPresentation.js';

/** the bar colors are the fill counterpart of the status tones and have to stay literal for the scanner */
const STATUS_BAR_CLASS: Record<string, string> = {
  editing: 'bg-info/70',
  draft: 'bg-warning/70',
  active: 'bg-success/70',
  partial: 'bg-success/40',
  future: 'bg-base-300',
  expired: 'bg-base-300',
  terminated: 'bg-error/60',
  inactive: 'bg-base-300',
};

const TONE_TEXT_CLASS: Record<string, string> = {
  success: 'text-success',
  warning: 'text-warning',
  error: 'text-error',
  info: 'text-info',
  neutral: 'text-base-content/70',
};

/** month ticks, thinned out to year ticks once the shown span gets long */
const buildTicks = (axisStart: Date, axisEnd: Date) => {
  const months = Math.max(differenceInCalendarMonths(axisEnd, axisStart), 1);
  // readable (text-sm) labels need ~80px each, so ticks thin out early instead of shrinking the type
  const step = months <= 6 ? 1 : months <= 18 ? 3 : months <= 42 ? 6 : 12;
  const ticks: Array<{ date: Date; label: string; isYear: boolean }> = [];

  let cursor = startOfMonth(axisStart);
  if (cursor < axisStart) cursor = addMonths(cursor, 1);

  while (cursor <= axisEnd) {
    const isYear = cursor.getMonth() === 0;
    ticks.push({
      date: cursor,
      label: step >= 12 ? formatCalendarDay(cursor, 'yyyy') : formatCalendarDay(cursor, 'MM/yy'),
      isYear,
    });
    cursor = addMonths(cursor, step);
  }

  return ticks;
};

/**
 * A compact overview of the contract runtime and the supplements on it.
 *
 * The dropdowns stay the way to pick something, this answers "what runs when" at a glance, which a list of
 * dates cannot. Collapsible, because it grows by one lane per supplement.
 */
export const ContractTimelineBar = stateful(
  (props: {
    contract: Maybe<TimelineContract>;
    selectedDate: Date;
    selectedSupplement: Maybe<TimelineSupplement>;
    isEditingSupplement?: boolean;
    onDateSelected: (date: Date) => void;
    onSupplementSelected: (supplement: TimelineSupplement) => void;
  }) => {
    const contract = props.contract;
    if (!contract) return nothing;

    const supplements = (contract.supplements ?? []) as TimelineSupplement[];
    const contractStart = contract.agInfos?.einstellungsdatum;
    if (!contractStart) return nothing;

    const today = todayAsCalendarDay();
    const selectedDay = toCalendarDay(props.selectedDate);

    const axisStart = resolveAxisStart(contractStart, supplements, today);
    const axisEnd = resolveAxisEnd({ contractEnd: contract.agInfos?.gekuendigtZum, supplements }, axisStart, today);
    const span = Math.max(axisEnd.getTime() - axisStart.getTime(), 1);

    // a long employment needs room per month, otherwise the bars turn into slivers. The track then scrolls
    // sideways instead of compressing everything into the card width.
    const monthCount = Math.max(differenceInCalendarMonths(axisEnd, axisStart), 1);
    const trackMinWidth = Math.max(monthCount * 14, 480);

    const toPercent = (date: Date) => ((date.getTime() - axisStart.getTime()) / span) * 100;
    const clamp = (value: number) => Math.min(Math.max(value, 0), 100);
    const inAxis = (date: Date) => date >= axisStart && date <= axisEnd;

    const handleTrackClick = (event: MouseEvent) => {
      const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
      if (!rect.width) return;
      const ratio = (event.clientX - rect.left) / rect.width;
      props.onDateSelected(toCalendarDay(new Date(axisStart.getTime() + ratio * span)));
    };

    const contractEnd = contract.agInfos?.gekuendigtZum;
    const contractLeft = clamp(toPercent(contractStart));
    const contractWidth = Math.max(clamp(toPercent(contractEnd ?? axisEnd) - contractLeft), 0.5);

    const ticks = buildTicks(axisStart, axisEnd);

    /**
     * Month grid plus the two date markers. All of it lives in one absolutely filled layer that is laid over
     * the rows, so the lines run through the whole timeline instead of being cut off at every row boundary.
     */
    const verticalLines = () => html`
      <div class="absolute inset-0" aria-hidden="true">
        ${repeat(
          ticks,
          tick => tick.date.getTime(),
          tick => html`
            <div
              class="absolute inset-y-0 w-px ${tick.isYear ? 'bg-base-300' : 'bg-base-200'}"
              style="left: ${clamp(toPercent(tick.date))}%"
            ></div>
          `,
        )}

        <div
          class="absolute inset-y-0 w-0.5 -translate-x-1/2 rounded-full bg-success"
          style="left: ${clamp(toPercent(selectedDay))}%"
        ></div>

        ${
          inAxis(today)
            ? html`
                <div
                  class="absolute inset-y-0 w-1 -translate-x-1/2 rounded-full bg-primary"
                  style="left: ${clamp(toPercent(today))}%"
                >
                  <!-- a head on the line, so it reads as a marker and not as another gridline -->
                  <span
                    class="absolute -top-2 left-1/2 h-2.5 w-2.5 -translate-x-1/2 rounded-full bg-primary ring-2 ring-base-100"
                  ></span>
                </div>
              `
            : nothing
        }
      </div>
    `;

    const bar = (interval: SectionInterval, supplement: TimelineSupplement, statusKey: string) => {
      if (!interval.start) return nothing;
      const left = clamp(toPercent(interval.start));
      const width = Math.max(clamp(toPercent(interval.end ?? axisEnd) - left), 0.6);
      const isSelected = supplement._id === props.selectedSupplement?._id;
      const number = `${supplement.nachtragsNummer}${
        supplement.subNachtragsnummer ? `.${supplement.subNachtragsnummer}` : ''
      }`;

      return html`
        <button
          class="tooltip absolute top-0 h-3 rounded-sm ${STATUS_BAR_CLASS[statusKey] ?? 'bg-base-300'} ${
            isSelected ? 'ring-2 ring-primary' : ''
          }"
          style="left: ${left}%; width: ${width}%"
          aria-label="Nachtrag ${number} ${SUPPLEMENT_SECTION_LABEL[interval.section]}"
          @click=${(event: Event) => {
            event.stopPropagation();
            props.onSupplementSelected(supplement);
          }}
          ${hoverTooltip()}
        >
          <span class="tooltip-content whitespace-nowrap">
            Nachtrag ${number} · ${SUPPLEMENT_SECTION_LABEL[interval.section]} ·
            ${formatCalendarDay(interval.start, 'dd.MM.yyyy')} –
            ${interval.end ? formatCalendarDay(interval.end, 'dd.MM.yyyy') : 'offen'}
          </span>
        </button>
      `;
    };

    return Collapse({
      className: 'border-0 bg-transparent shadow-none',
      heading: html`
        <span class="flex flex-row flex-wrap items-center gap-3 text-sm">
          <span class="font-semibold text-base-content/70">Verlauf</span>
          <span class="flex flex-row items-center gap-1 text-base-content/70">
            <span class="inline-block h-3 w-0.5 bg-success"></span>
            ${formatCalendarDay(selectedDay, 'dd.MM.yyyy')}
          </span>
          ${
            inAxis(today)
              ? html`
                  <span class="flex flex-row items-center gap-1 text-base-content/70">
                    <span class="inline-block h-3 w-1 rounded-full bg-primary"></span> Heute
                  </span>
                `
              : nothing
          }
        </span>
      `,
      content: html`
        <div class="overflow-x-auto">
          <div class="pt-8" style="min-width: ${trackMinWidth}px">
            <!--
              Every row shares the same track geometry (gutters left and right for the labels), so the markers
              can be drawn once as a single overlay running through all rows instead of per row.
            -->
            <!--
              Rows and the marker layer share one grid cell, so the layer is stretched to exactly the height of
              all rows together and the lines can run through them without depending on an auto height.
            -->
            <div class="grid pr-6 pl-9">
              <div class="flex flex-col gap-1" style="grid-area: 1 / 1">
                <!-- month / year scale -->
                <div class="relative h-5 select-none">
                  ${repeat(
                    ticks,
                    tick => `label-${tick.date.getTime()}`,
                    tick => html`
                      <span
                        class="absolute top-0 -translate-x-1/2 text-sm whitespace-nowrap text-base-content/70 ${
                          tick.isYear ? 'font-semibold' : ''
                        }"
                        style="left: ${clamp(toPercent(tick.date))}%"
                      >
                        ${tick.label}
                      </span>
                    `,
                  )}
                </div>

                <!-- contract runtime, click to pick a date -->
                <div class="relative h-5 cursor-pointer" @click=${handleTrackClick}>
                  <div class="absolute inset-x-0 top-2 h-1 rounded bg-base-200"></div>
                  <div
                    class="absolute top-1 h-3 rounded bg-primary/25"
                    style="left: ${contractLeft}%; width: ${contractWidth}%"
                  ></div>
                </div>

                <!-- one lane per supplement -->
                ${repeat(
                  supplements,
                  supplement => supplement._id,
                  supplement => {
                    const { status, changedSections } = getSupplementStatusForDate(supplement, props.selectedDate, {
                      isBeingEdited: props.isEditingSupplement && supplement._id === props.selectedSupplement?._id,
                    });
                    const presentation = SUPPLEMENT_STATUS_PRESENTATION[status];

                    return html`
                      <div class="relative h-3 cursor-pointer" @click=${handleTrackClick}>
                        <span class="absolute -left-9 w-8 text-right text-sm leading-none text-base-content/70">
                          ${supplement.nachtragsNummer}${
                            supplement.subNachtragsnummer ? `.${supplement.subNachtragsnummer}` : ''
                          }
                        </span>
                        ${sectionIntervals(supplement)
                          .filter(interval => changedSections.includes(interval.section))
                          .map(interval => bar(interval, supplement, status))}
                        <span
                          class="absolute -right-5 w-4 ${presentation.iconClass} ${TONE_TEXT_CLASS[presentation.tone]}"
                          title=${presentation.label}
                        ></span>
                      </div>
                    `;
                  },
                )}
              </div>

              <!-- gridlines and the two date markers, drawn through every row at once -->
              <div class="pointer-events-none relative" style="grid-area: 1 / 1">${verticalLines()}</div>
            </div>

            <div class="flex flex-row justify-between pt-1 text-sm text-base-content/70">
              <span>${formatCalendarDay(axisStart, 'dd.MM.yyyy')}</span>
              <span>
                ${
                  contractEnd
                    ? formatCalendarDay(contractEnd, 'dd.MM.yyyy')
                    : `offenes Ende · Skala bis ${formatCalendarDay(axisEnd, 'yyyy')}`
                }
              </span>
            </div>
          </div>
        </div>
      `,
    });
  },
);
